import React, { useState } from 'react';
import { Truck, X } from 'lucide-react';

interface ArrivalRow {
  date: string;
  qty?: number | null;
}

interface Props {
  prossimArrivo: string;
  qtaArrivo?: number | null;
  /** In futuro si potrà passare un array di arrivi multipli */
  arrivals?: ArrivalRow[];
  /**
   * - `compact`  → pill piccolo (lista)
   * - `default`  → pill standard
   * - `text`     → solo testo sottolineato cliccabile, niente pill/icona
   * - `muted`    → testo grigio "Prossimi arrivi" cliccabile, niente pill/icona
   *                (usato per prodotti DISPONIBILI con arrivo programmato,
   *                per non confonderlo con l'indicatore rosso "Esaurito")
   */
  variant?: 'compact' | 'default' | 'text' | 'muted';
}

/** Formatta una data NTS (YYYYMMDD, YYYY-MM-DD o ISO) → "DD/MM/YYYY" */
function formatDate(raw: string): string {
  if (!raw) return raw;
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 8) {
    return `${digits.slice(6, 8)}/${digits.slice(4, 6)}/${digits.slice(0, 4)}`;
  }
  try {
    const dt = new Date(raw);
    if (!isNaN(dt.getTime())) {
      return dt.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  } catch { /* ignore */ }
  return raw;
}

export function StockArrivalBadge({ prossimArrivo, qtaArrivo, arrivals, variant = 'default' }: Props) {
  const [open, setOpen] = useState(false);

  /** Normalizza: usa `arrivals` se passato, altrimenti crea array da singoli campi */
  const rows: ArrivalRow[] = arrivals?.length
    ? arrivals
    : [{ date: prossimArrivo, qty: qtaArrivo }];

  const isCompact = variant === 'compact';
  const isText    = variant === 'text';
  const isMuted   = variant === 'muted';

  const triggerCls = isMuted
    ? 'block text-left self-start underline font-medium text-muted-foreground text-xs cursor-pointer bg-transparent border-0 p-0 hover:text-foreground transition-colors'
    : isText
    ? 'underline font-semibold text-amber-600 text-[10px] cursor-pointer bg-transparent border-0 p-0 hover:text-amber-700 transition-colors'
    : `inline-flex items-center gap-1 rounded-full border font-semibold
       transition-colors cursor-pointer bg-orange-50 border-orange-200 text-orange-700
       hover:bg-orange-100 hover:border-orange-300
       ${isCompact ? 'text-[9px] px-1.5 py-0.5' : 'text-[10px] px-2.5 py-1'}`;

  return (
    <>
      <button
        type="button"
        onClick={e => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        data-testid="stock-arrival-badge"
        title="Vedi prossimo arrivo stock"
        className={triggerCls}
      >
        {!isText && !isMuted && <Truck className={isCompact ? 'w-2.5 h-2.5 flex-none' : 'w-3 h-3 flex-none'} />}
        {isMuted ? 'Prossimi arrivi' : 'In arrivo'}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm"
            onClick={e => e.stopPropagation()}
            data-testid="stock-arrival-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#f0f0ed]">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Truck className="w-4 h-4 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-[16px] font-semibold text-[#1f1f1d]">Prossimi arrivi</h3>
                  <p className="text-[12px] text-[#5c5c57]">Disponibilità prevista in magazzino</p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="stock-arrival-modal-close"
                className="text-[#9b9b96] hover:text-[#3a3a36] transition-colors p-1 -mr-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Tabella */}
            <div className="px-5 py-4">
              <table className="w-full text-[14px]">
                <thead>
                  <tr className="border-b border-[#e8e8e4]">
                    <th className="text-left pb-2 text-[11px] font-bold tracking-widest text-[#5c5c57] font-mono uppercase">
                      Data prevista
                    </th>
                    <th className="text-right pb-2 text-[11px] font-bold tracking-widest text-[#5c5c57] font-mono uppercase">
                      Qtà in arrivo
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} className="border-b border-[#f3f3f0] last:border-0">
                      <td className="py-2.5 font-semibold text-[#1f1f1d]">
                        {formatDate(row.date)}
                      </td>
                      <td className="py-2.5 text-right">
                        {row.qty != null && row.qty > 0 ? (
                          <span className="font-semibold text-[#1f1f1d]">
                            {row.qty}{' '}
                            <span className="font-normal text-[#5c5c57]">pz</span>
                          </span>
                        ) : (
                          <span className="text-[#5c5c57] italic text-[13px]">n.d.</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="px-5 pb-4">
              <p className="text-[12px] text-[#5c5c57] text-center">
                Date indicative — soggette a variazioni del fornitore.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
